/**
 * Utilities for validating and handling coordinates in Leaflet markers
 * Prevents "Invalid LatLng object: (undefined, undefined)" errors
 */

/**
 * Validates if a coordinate value is a valid number for lat/lng
 * @param {any} value - The coordinate value to validate
 * @returns {boolean} - True if valid coordinate number
 */
export function isValidCoordinate(value) {
  return typeof value === 'number' && 
         !isNaN(value) && 
         isFinite(value);
}

/**
 * Validates if latitude is within valid range (-90 to 90)
 * @param {any} lat - Latitude value to validate
 * @returns {boolean} - True if valid latitude
 */
export function isValidLatitude(lat) {
  return isValidCoordinate(lat) && lat >= -90 && lat <= 90;
}

/**
 * Validates if longitude is within valid range (-180 to 180)
 * @param {any} lng - Longitude value to validate
 * @returns {boolean} - True if valid longitude
 */
export function isValidLongitude(lng) {
  return isValidCoordinate(lng) && lng >= -180 && lng <= 180;
}

/**
 * Validates a coordinate pair [lat, lng] for Leaflet marker usage
 * @param {any} lat - Latitude value
 * @param {any} lng - Longitude value
 * @returns {boolean} - True if both coordinates are valid
 */
export function isValidLatLng(lat, lng) {
  return isValidLatitude(lat) && isValidLongitude(lng);
}

/**
 * Validates coordinates from GeoJSON geometry format [lng, lat]
 * @param {any} coordinates - Array with [longitude, latitude] or undefined
 * @returns {boolean} - True if coordinates are valid
 */
export function isValidGeoJSONCoordinates(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return false;
  }
  const [lng, lat] = coordinates;
  return isValidLatLng(lat, lng);
}

/**
 * Safely extracts [lat, lng] position from GeoJSON coordinates [lng, lat]
 * @param {any} coordinates - GeoJSON coordinates array [lng, lat]
 * @param {string} debugContext - Context for logging (e.g., 'navaids', 'marker')
 * @returns {[number, number] | null} - [lat, lng] position or null if invalid
 */
export function safeGetLatLngFromGeoJSON(coordinates, debugContext = 'unknown') {
  if (!isValidGeoJSONCoordinates(coordinates)) {
    console.warn(`[CoordinateUtils] Invalid coordinates in ${debugContext}:`, coordinates);
    return null;
  }
  
  const [lng, lat] = coordinates;
  return [lat, lng];
}

/**
 * Safely extracts position from point object with x,y properties
 * @param {object} point - Point object with x and y properties
 * @param {string} debugContext - Context for logging
 * @returns {[number, number] | null} - [lat, lng] position or null if invalid
 */
export function safeGetLatLngFromPoint(point, debugContext = 'unknown') {
  if (!point || !isValidLatLng(point.y, point.x)) {
    console.warn(`[CoordinateUtils] Invalid point coordinates in ${debugContext}:`, point);
    return null;
  }
  
  return [point.y, point.x];
}

/**
 * Filters an array of GeoJSON features to only include those with valid coordinates
 * @param {Array} features - Array of GeoJSON features
 * @param {string} debugContext - Context for logging
 * @returns {Array} - Filtered array with only valid coordinate features
 */
export function filterValidGeoJSONFeatures(features, debugContext = 'unknown') {
  if (!Array.isArray(features)) {
    console.warn(`[CoordinateUtils] Features is not an array in ${debugContext}:`, features);
    return [];
  }

  const validFeatures = features.filter(feature => {
    if (!feature?.geometry?.coordinates) {
      console.warn(`[CoordinateUtils] Feature missing geometry/coordinates in ${debugContext}:`, feature);
      return false;
    }
    return isValidGeoJSONCoordinates(feature.geometry.coordinates);
  });

  return validFeatures;
}

/**
 * Provides a safe default empty array for potentially null/undefined data
 * @param {any} data - Data that might be null/undefined
 * @param {string} property - Property name to access if data is object (optional)
 * @returns {Array} - Safe array, empty if data is invalid
 */
export function safeArray(data, property = null) {
  if (property && data && typeof data === 'object') {
    return Array.isArray(data[property]) ? data[property] : [];
  }
  return Array.isArray(data) ? data : [];
}
/**
 * Converts an array of UTM {x, y} points to Leaflet [lat, lon] pairs using the
 * specified EPSG code (e.g. "EPSG:32633").  The EPSG must be the one that was
 * used when computing the coordinates so that the UTM zone is always the one
 * determined by the navaid position, never auto-selected from the vertex longitude.
 *
 * Falls back to returning null if proj4 is unavailable or the EPSG is missing.
 *
 * @param {Array<{x:number,y:number}>} utmPts   – Array of UTM easting/northing points
 * @param {string}                     epsgCode – EPSG string, e.g. "EPSG:32633"
 * @returns {Array<[number,number]>|null}        – [lat, lon] pairs for Leaflet, or null
 */
export function utmPolyToLatLon(utmPts, epsgCode) {
  if (!utmPts || !utmPts.length || !epsgCode) return null;
  try {
    const proj4 = _getProj4();
    if (!proj4) return null;
    return utmPts.map(pt => {
      const [lon, lat] = proj4(epsgCode, 'EPSG:4326', [pt.x, pt.y]);
      return [lat, lon];
    });
  } catch (_) {
    return null;
  }
}

/**
 * Expand a LWPOLYLINE-style vertex array (with DXF bulge values) into a smooth
 * sequence of [lat, lon] positions suitable for Leaflet <Polygon>.
 *
 * Each vertex has the shape: { utm_x, utm_y, bulge, lat, lon }
 * The `bulge` on vertex[i] is the arc from vertex[i] to vertex[i+1].
 * bulge = tan(α/4) where α is the included angle; positive = CCW, negative = CW.
 *
 * Segments with |bulge| < 1e-6 are kept as straight lines (no interpolation).
 * Arc segments are expanded into N evenly-spaced intermediate points computed in
 * UTM space (accurate) then converted to lat/lon via proj4.
 *
 * Falls back to straight-line positions if proj4 is unavailable or epsgCode is missing.
 *
 * @param {Array<{utm_x,utm_y,bulge,lat,lon}>} vertices – P-point vertices from backend
 * @param {string}                              epsgCode – EPSG string, e.g. "EPSG:32632"
 * @returns {Array<[number,number]>}                     – [lat, lon] positions for Leaflet
 */
export function expandBulgePolylineToLatLon(vertices, epsgCode) {
  if (!vertices || vertices.length === 0) return [];

  // Resolve proj4 once; fall back to straight-line if unavailable
  const proj4 = _getProj4();
  const utmToLatLon = (x, y) => {
    if (proj4 && epsgCode) {
      try {
        const [lon, lat] = proj4(epsgCode, 'EPSG:4326', [x, y]);
        return [lat, lon];
      } catch (_) { /* fall through */ }
    }
    // Fallback: return the pre-converted lat/lon from the nearest vertex
    return null;
  };

  const positions = [];
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const v = vertices[i];
    const next = vertices[(i + 1) % n];

    // Always emit the current vertex
    positions.push([v.lat, v.lon]);

    const b = v.bulge;
    if (!b || Math.abs(b) < 1e-6) continue; // straight segment, nothing to add

    // Arc from v to next in UTM
    const x1 = v.utm_x, y1 = v.utm_y;
    const x2 = next.utm_x, y2 = next.utm_y;
    const dx = x2 - x1, dy = y2 - y1;
    const chord = Math.sqrt(dx * dx + dy * dy);
    if (chord < 1e-9) continue;

    const absB = Math.abs(b);
    const r = chord * (1 + b * b) / (4 * absB);

    // Signed offset from chord midpoint to arc center along the LEFT perpendicular.
    // Formula: s = chord*(1-b²)/(4b)  — same convention as backend deCenterFromBulge.
    // Positive b → center LEFT of travel; negative b → center RIGHT.
    // Handles |b| > 1 automatically (sign of 1-b² flips the side).
    const s = chord * (1 - b * b) / (4 * b);

    // LEFT unit vector perpendicular to P1→P2 direction (rotate travel vector 90° CCW).
    // Matches backend: perpX = -dy/chord, perpY = dx/chord.
    const perpX = -dy / chord, perpY = dx / chord;

    const cx = (x1 + x2) / 2 + s * perpX;
    const cy = (y1 + y2) / 2 + s * perpY;

    const startAngle = Math.atan2(y1 - cy, x1 - cx);
    // Total sweep angle (always positive).
    const totalSweep = 4 * Math.atan(absB);

    // Number of interpolation steps proportional to arc size (min 8, max 64)
    const steps = Math.min(64, Math.max(8, Math.ceil(totalSweep * 32 / Math.PI)));

    // Angular traversal direction in standard-math angle space:
    // CW bearing (b < 0) ↔ decreasing standard-math angle → dir = -1 = sign(b).
    // CCW bearing (b > 0) ↔ increasing standard-math angle → dir = +1 = sign(b).
    const dir = Math.sign(b);

    // Emit intermediate points (skip first = P1 already added; skip last = P2 added next iteration)
    for (let sStep = 1; sStep < steps; sStep++) {
      const angle = startAngle + dir * totalSweep * (sStep / steps);
      const px = cx + r * Math.cos(angle);
      const py = cy + r * Math.sin(angle);
      const ll = utmToLatLon(px, py);
      if (ll) {
        positions.push(ll);
      }
    }
  }

  return positions;
}

let _proj4Instance = null;
function _getProj4() {
  if (_proj4Instance) return _proj4Instance;
  try {
    // proj4 may export default differently depending on bundler
    let p = require('proj4');
    if (p && p.default) p = p.default;
    _proj4Instance = p;
    return _proj4Instance;
  } catch (_) {
    return null;
  }
}
